import { useMemo } from "react";
import type { LatLngExpression } from "leaflet";
import { ConnectProto, enumToJson } from "@racoon/api";
import { GpsTrackingPoint } from "../model/types";
import { MapObject } from "~/features/map-view/ui/map-view";

/**
 * The truck's type as its proto JSON name (e.g. "TRUCK_TYPE_MSW") — the form
 * the marker sprites are keyed by, same as the dashboard map feeds them.
 *
 * `enumToJson` throws on values outside the enum, and `TRUCK_TYPE_UNDEFINED`
 * has no sprite of its own, so both fall back to the generic marker.
 */
const truckTypeName = (truck: ConnectProto.Truck): string | undefined => {
  const type = truck.truckType?.type || truck.type;
  if (!type) return undefined;
  try {
    return enumToJson(ConnectProto.TruckTypeSchema, Number(type)) as string;
  } catch {
    return undefined;
  }
};

/**
 * Hook to transform GPS points into objects for map display.
 *
 * A GPS point carries no vehicle type, so `trucks` supplies it — without it
 * every marker would fall back to the generic bunker sprite.
 */
export function useMapTrucks(
  gpsPoints: GpsTrackingPoint[],
  trucks: ConnectProto.Truck[] = [],
): MapObject[] {
  return useMemo(() => {
    const truckTypeById = new Map(trucks.map((truck) => [String(truck.id), truckTypeName(truck)]));

    // Group GPS points by vehicle and get last position for each
    const vehicleMap = new Map<string, GpsTrackingPoint>();

    gpsPoints.forEach((point) => {
      const existing = vehicleMap.get(point.vehicleId);
      if (!existing || point.timestamp > existing.timestamp) {
        vehicleMap.set(point.vehicleId, point);
      }
    });

    return Array.from(vehicleMap.values()).map((point) => ({
      type: "truck" as const,
      name: point.plateNumber,
      id: point.vehicleId,
      lat: point.lat,
      lon: point.lon,
      truckType: truckTypeById.get(point.vehicleId),
    }));
  }, [gpsPoints, trucks]);
}

/**
 * Hook to group GPS points by vehicle and trip for creating routes
 */
export function useRoutePaths(gpsPoints: GpsTrackingPoint[]): Array<{
  vehicleId: string;
  tripId?: string;
  path: LatLngExpression[];
}> {
  return useMemo(() => {
    const routes: Array<{
      vehicleId: string;
      tripId?: string;
      path: LatLngExpression[];
    }> = [];

    // Group by vehicle and trip
    const grouped = new Map<string, Map<string, GpsTrackingPoint[]>>();

    gpsPoints.forEach((point) => {
      const vehicleKey = point.vehicleId;
      const tripKey = point.tripId || "default";

      if (!grouped.has(vehicleKey)) {
        grouped.set(vehicleKey, new Map());
      }

      const vehicleTrips = grouped.get(vehicleKey)!;
      if (!vehicleTrips.has(tripKey)) {
        vehicleTrips.set(tripKey, []);
      }

      vehicleTrips.get(tripKey)!.push(point);
    });

    // Create routes for each vehicle/trip combination
    grouped.forEach((vehicleTrips, vehicleId) => {
      vehicleTrips.forEach((points, tripId) => {
        const sortedPoints = points.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        if (sortedPoints.length > 1) {
          routes.push({
            vehicleId,
            tripId: tripId !== "default" ? tripId : undefined,
            path: sortedPoints.map((p) => [p.lat, p.lon] as LatLngExpression),
          });
        }
      });
    });

    return routes;
  }, [gpsPoints]);
}

/**
 * Хук для получения центра карты на основе выбранного транспорта или последней GPS точки
 */
export function useMapCenter(
  selectedVehicle: { lat?: number; lon?: number } | null | undefined,
  gpsPoints: GpsTrackingPoint[],
  defaultCenter: [number, number] = [43.238949, 76.889709],
): [number, number] {
  return useMemo(() => {
    if (selectedVehicle?.lat && selectedVehicle?.lon) {
      return [selectedVehicle.lat, selectedVehicle.lon];
    }
    if (gpsPoints.length > 0) {
      const lastPoint = gpsPoints[gpsPoints.length - 1];
      return [lastPoint.lat, lastPoint.lon];
    }
    return defaultCenter;
  }, [selectedVehicle, gpsPoints, defaultCenter]);
}
